import BadgeCounterModelsMobile from '@/@straico-ui/commons/BagdeCounterModelsMobile';
import useChatStore, { REMOVE_MODEL } from '@/Components/Chat/store';
import CombosModalSelector from '@/Features/App/Components/CombosModalSelector/CombosModalSelector';
import useGetModelsIcons from '@/Hooks/useGetModelsIcons';
import useIsMobile from '@/Hooks/useIsMobile';
import { cn } from '@/utils/styles';
import { EllipsisHorizontalIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Badge, Tooltip } from '@mui/material';
import { memo, useState } from 'react';

const CombosSelector = () => {
  const isMobile = useIsMobile();
  const { getIcon } = useGetModelsIcons();

  const [anchorEl, setAnchorEl] = useState(null);
  const {
    combos,
    combosLoading,
    selectedCombo,
    selectedModels,
    addEvent,
    allModels,
    updateSelectedModels,
    setSelectedCombo,
    selectedCapabilities: capabilities,
    setShowModelsDialog,
  } = useChatStore();

  const checkCapabilities = (model) => {
    if (model.function_calls && model.function_calls.length) {
      const modelCapabilities = capabilities.filter((capability) =>
        model.function_calls
          .map((func) => func.capability)
          .includes(capability._id)
      );
      return modelCapabilities;
    }
    return null;
  };

  const activeCapability = (model) => {
    const cap = checkCapabilities(model);
    return cap && cap.length;
  };

  const joinCapabilities = (model) => {
    const capabilitiesList = (checkCapabilities(model) || []).map(
      (c) => c.name + ' capability enabled'
    );
    return [model.name, ...capabilitiesList].join('\n');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleComboClick = async (combo) => {
    setSelectedCombo(combo);

    if (combo && Array.isArray(combo.llms)) {
      try {
        const selectedModels = combo.llms
          .map((llmId) => allModels.find((m) => m._id === llmId))
          .filter(Boolean)
          .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

        updateSelectedModels(() => selectedModels);
        localStorage.setItem('currentModels', JSON.stringify(selectedModels));
      } catch (error) {
        console.error('Error processing combo:', error);
      }
    }
    handleMenuClose();
  };

  const handleOpenModelsDialog = () => {
    setShowModelsDialog(true);
  };

  const modelsHasActiveCapabilities = selectedModels.some((model) =>
    activeCapability(model)
  );

  return (
    <div className="flex gap-2 flex-row justify-center items-center">
      <Tooltip
        arrow
        placement="right"
        title={selectedCombo?.name || 'No combo selected'}
      >
        <div
          className={cn(
            'flex items-center justify-center cursor-pointer relative gap-1 p-1 bg-white dark:bg-night-black rounded-lg shadow-lg mt-1'
          )}
          onClick={handleMenuOpen}
        >
          <span className="text-sm font-bold flex items-center justify-center text-[#9091A4]">
            {selectedCombo?.name || '-'}
          </span>
          <EllipsisHorizontalIcon
            className={`size-4 transition-transform duration-300 text-[#9091A4]`}
          />
        </div>
      </Tooltip>
      {/* Collapsed combos menu */}
      <CombosModalSelector
        combos={combos}
        anchorEl={anchorEl}
        customWidth="320px"
        loading={combosLoading}
        setAnchorEl={setAnchorEl}
        handleMenuClose={handleMenuClose}
        handleComboClick={handleComboClick}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      />
      <div className="flex gap-2">
        {isMobile ? (
          <BadgeCounterModelsMobile
            count={selectedModels.length ?? 0}
            capabilities={modelsHasActiveCapabilities}
            onClick={handleOpenModelsDialog}
          />
        ) : (
          <>
            {selectedModels.map((model, index) => (
              <div
                className="relative group"
                key={index}
                onClick={handleOpenModelsDialog}
              >
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={!activeCapability(model)}
                >
                  <Tooltip
                    className="flex gap-2 cursor-pointer select-none"
                    title={
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {joinCapabilities(model)}
                      </span>
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#D4D5E6',
                          color: '#5256A6',
                          '& .MuiTooltip-arrow': {
                            color: '#D4D5E6',
                          },
                        },
                      },
                    }}
                  >
                    <div>{getIcon(model.icon)}</div>
                  </Tooltip>
                </Badge>

                {selectedModels.length > 1 && (
                  <div
                    className="hidden group-hover:block absolute z-50 p-[1px] rounded cursor-pointer select-none -left-1 -bottom-1 bg-lavender dark:bg-ship-grey text-violet-blue dark:text-stargate-shimmer"
                    onClick={(e) => {
                      e.stopPropagation();
                      addEvent(REMOVE_MODEL, model);
                    }}
                  >
                    <XMarkIcon className="w-[10px] h-[10px] stroke-[3px]" />
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(CombosSelector);
